import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';

import { MINDSET_IMAGE_URL } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { Mindset as MindsetEnum } from '@/types';
import { Button, Drawer, Skeleton, Typography } from '@/ui';
import { track } from '@/utils';

import Item from './Item';

import {
  requestMindsetBackground,
  useMindsetBackgroundState,
} from '@/components/Layouts/MindsetBackground/store';
import { useUpdateUserMutation } from '@/store/api/userApi';

// True only once `value` has stayed true for `delayMs`, so quick loads never flicker.
const useDelayedFlag = (value: boolean, delayMs: number) => {
  const [flag, setFlag] = useState(false);

  useEffect(() => {
    if (!value) {
      setFlag(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setFlag(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return flag;
};

const noop = () => {};

const Mindset = () => {
  const { user, isUserLoading, refetchUser } = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(MindsetEnum.reflective);
  const [updateUser] = useUpdateUserMutation();
  const { isLoading: isBackgroundLoading } = useMindsetBackgroundState();
  const showPulse = useDelayedFlag(isBackgroundLoading, 300);

  // Saves can overlap (the drawer can be reopened while one is in flight), so:
  // - `latestRequest` fences UI updates: only the newest selection may roll back or
  //   clear the background request.
  // - `saveQueue` sends the PUTs in click order, so the last click is the last write.
  // - `persistedMindset` is what the server holds after the last successful save.
  const latestRequest = useRef(0);
  const pendingSaves = useRef(0);
  const persistedMindset = useRef(user.mindset);
  // True while a save has succeeded that the current-user cache hasn't been re-read for.
  const needsReconcile = useRef(false);
  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    // Don't let a refetch from an older save overwrite a newer optimistic selection.
    if (pendingSaves.current > 0) return;
    persistedMindset.current = user.mindset;
    setName(user.mindset);
  }, [user]);

  const onButtonClick = () => {
    track('Open Mindset Background', { userId: user.id });
    setIsOpen(true);
  };

  const onItemClick = (mindset: MindsetEnum) => async () => {
    latestRequest.current += 1;
    const requestId = latestRequest.current;
    const isLatest = () => requestId === latestRequest.current;
    pendingSaves.current += 1;

    track('Selected Mindset', { userId: user.id, mindset });
    setIsOpen(false);
    setName(mindset);
    requestMindsetBackground(mindset);

    // Confirmation reads live inside the queue, so a later PUT never starts while an
    // earlier current-user GET is still in flight (RTK Query could otherwise join the
    // two and restore stale data after the newer save committed).
    const readUser = async () => {
      try {
        // `refetch` resolves to an error-bearing result instead of rejecting, so unwrap
        // it: only a real success may mark the user cache as fresh.
        await refetchUser().unwrap();
        needsReconcile.current = false;
      } catch {
        // Best effort: the label and background keep showing the saved selection.
      }
    };

    // A task skips its read when a newer selection exists; that selection's own task
    // (or the reconcile below, if it fails) reads instead.
    const outcome = { saved: false };
    const run = saveQueue.current.then(async () => {
      await updateUser({ id: user.id, user: { mindset } }).unwrap();
      outcome.saved = true;
      persistedMindset.current = mindset;
      needsReconcile.current = true;
      if (isLatest()) await readUser();
    });
    saveQueue.current = run.catch(noop);

    try {
      await run;
    } catch {
      // Roll back only the newest selection, to what the server actually holds.
      if (isLatest()) {
        setName(persistedMindset.current);
        requestMindsetBackground(persistedMindset.current);
        // An earlier save may have succeeded with its read skipped, so the user cache
        // is stale: re-read it (queued, so it can't overlap a newer PUT).
        if (needsReconcile.current) {
          saveQueue.current = saveQueue.current.then(readUser);
          await saveQueue.current;
        }
      }
    }

    pendingSaves.current -= 1;
    if (isLatest() && !needsReconcile.current) {
      // The user cache is fresh again, so the background follows it instead of the
      // pinned selection. If the read failed it stays pinned to the persisted value.
      requestMindsetBackground(null);
    }
  };

  const onDrawerClose = () => {
    track('Dismissed Mindset Background', { userId: user.id });
    setIsOpen(false);
  };

  if (isUserLoading || !user.id) {
    return <Skeleton />;
  }

  return (
    <>
      <Button
        className="hidden w-fit shrink-0 cursor-pointer items-center gap-2 rounded-full py-2 pl-2 pr-4 transition ease-in-out hover:bg-cement-600/30 md:flex"
        onClick={onButtonClick}
      >
        <img
          alt="mindset"
          className={clsx('size-10', { 'animate-pulse': showPulse })}
          src={`${MINDSET_IMAGE_URL}mindset.svg`}
        />
        <div className="flex flex-col text-left text-white">
          <Typography className="cursor-pointer" variant="caption">
            Mindset
          </Typography>
          <Typography className="cursor-pointer capitalize" variant="subtitle1">
            {name}
          </Typography>
        </div>
      </Button>
      <Drawer anchor="right" open={isOpen} onClose={onDrawerClose}>
        <div className="bg-opacity/60 flex flex-1 flex-col gap-4 overflow-auto bg-cement-600 p-4">
          {Object.keys(MindsetEnum).map((key) => (
            <Item
              key={`mindset-selectable-item-${key}`}
              isSelected={key === name}
              mindset={key as MindsetEnum}
              onClick={onItemClick(key as MindsetEnum)}
            />
          ))}
        </div>
      </Drawer>
    </>
  );
};

export default Mindset;
